import React from 'react';
import { useTheme } from '@/contexts/ThemeContext';
import TopNavigation from '../components/TopNavigation';

interface PrivacyPageProps {
  onNavigateToExplore?: () => void;
  onNavigateToLibrary?: () => void;
  onNavigateToProfile?: () => void;
  onNavigateToMyBooks?: () => void;
  onNavigateHome?: () => void;
  onNavigateToLogin?: (source?: string) => void;
  onNavigateToSignup?: (source?: string) => void;
  onNavigateToTerms?: () => void;
  onToggleMenu: () => void;
  isAuthenticated: boolean;
}

/** One purpose we process a category of personal data for, and the basis for it. */
interface Use {
  purpose: string;
  basis: string;
}

interface DataCategory {
  category: string;
  uses: Use[];
}

const CONTRACT = 'The processing is necessary for the performance of a contract with you.';
const CONSENT =
  'We will only process your personal data in this way to the extent that you have given us your consent to do so.';
const legitimateInterest = (interest: string) =>
  `The processing is necessary for our legitimate interests, namely ${interest}.`;
const consentOrInterest = (interest: string) =>
  'Where required by applicable law, we will process your personal data for these purposes only to the extent ' +
  'that you have given us consent to do so. Where applicable law permits, the processing is necessary for our ' +
  `legitimate interests, namely ${interest}.`;

const IMPROVEMENT = consentOrInterest('identifying and analysing errors and service improvement');
const PREFERENCES = consentOrInterest(
  'presenting our Services to you in a way that meets your preferences'
);

const COLLECTED_DATA: DataCategory[] = [
  {
    category:
      'Account Information, including first and last name, email address, account credentials or one-time passcodes.',
    uses: [
      {
        purpose:
          'To set up your account profile, authenticate you on our Services, and facilitate provision of the Services.',
        basis: CONTRACT,
      },
      {
        purpose: 'To communicate with you regarding your account and use of the Services.',
        basis: CONTRACT,
      },
      {
        purpose: 'To facilitate provision of support services in relation to your use of the Services.',
        basis: CONTRACT,
      },
      {
        purpose:
          'To send notifications and alerts to you, if you have signed up to receive marketing communications.',
        basis: CONSENT,
      },
    ],
  },
  {
    category:
      'Profile Information, including username, profile picture, reading preferences, listening history, and bookmarks.',
    uses: [
      {
        purpose: 'To personalise your experience and facilitate provision of the Services.',
        basis: CONTRACT,
      },
      {
        purpose: 'To personalise the notifications and alerts we send to you.',
        basis: legitimateInterest('personalising the Services to our users'),
      },
    ],
  },
  {
    category:
      'User Content, including voice recordings and voice commands, questions you ask about book content, notes, annotations, and other content you input or transmit through our iOS app.',
    uses: [
      {
        purpose:
          'To provide you with our Services, such as playing audiobooks, processing your voice commands, and answering your questions about book content.',
        basis: CONTRACT,
      },
      {
        purpose:
          "To develop, operate, improve, maintain, protect, and provide the features and functionality of our Services, including by training or fine-tuning our and our third-party providers' artificial intelligence and machine learning models.",
        basis: IMPROVEMENT,
      },
    ],
  },
  {
    category:
      'Payment Information, including payment card information, billing address, and other financial information. While we do not process payments ourselves, we may receive the name on the card, the last four digits of your card number, and expiry date from the payments provider.',
    uses: [
      { purpose: 'To facilitate and fulfil your request to use the Services.', basis: CONTRACT },
      {
        purpose: 'To identify and prevent fraud.',
        basis: legitimateInterest('identifying and preventing fraud'),
      },
    ],
  },
  {
    category:
      "Voice Data, including audio recordings of your voice when you use the app's voice command and question-answering features.",
    uses: [
      {
        purpose:
          'To transcribe your speech, understand your questions, and provide you with relevant answers based on the audiobook content.',
        basis: CONTRACT,
      },
      {
        purpose: 'To improve our speech recognition accuracy and response quality.',
        basis: CONSENT,
      },
    ],
  },
  {
    category:
      'Feedback and Support Information, including the contents of messages sent through forms, chat platforms, email addresses, or other contact information we make available to users.',
    uses: [
      {
        purpose:
          'To handle your customer services requests, including directing requests to appropriate team members, and investigating and addressing any concerns.',
        basis: CONTRACT,
      },
      {
        purpose: 'To respond to your general enquiries about our Services.',
        basis: legitimateInterest('responding to enquiries from prospective users'),
      },
      {
        purpose: 'To identify ways to improve our Services.',
        basis: legitimateInterest(
          'to understand how our Services are used and to grow our platform'
        ),
      },
    ],
  },
  {
    category: 'Social Media Data, may include profile information, profile picture, and username.',
    uses: [
      {
        purpose:
          'When you interact with our Services through social media networks, we may receive some information that you permit the social network to share with third parties. The data we receive depends upon your privacy settings with the social network. You should always review and, if necessary, adjust your privacy settings on third-party websites and social media networks and services before sharing information.',
        basis: CONSENT,
      },
    ],
  },
  {
    category:
      'Device and Network Data, including the device type, manufacturer, and model, operating system, IP address, browser type, Internet service provider, and unique identifiers associated with you, your device, or your network.',
    uses: [
      {
        purpose: 'To operate our Services and present them to you in the correct format for your device.',
        basis: CONTRACT,
      },
      {
        purpose: 'To detect suspicious activity on our Services and fraudulent use of the Services.',
        basis: legitimateInterest('ensuring the security and integrity of the Services'),
      },
      {
        purpose:
          'To analyse the performance of the Services and to identify errors and ways in which we can improve the Services.',
        basis: IMPROVEMENT,
      },
      {
        purpose: 'To allow us, and our partners, to present advertising that is tailored to you.',
        basis: CONSENT,
      },
    ],
  },
  {
    category:
      'Usage and Browsing Information, including the site from which you came, the site to which you are going when you leave our Services, how frequently you access our Services, browsing behaviour, and actions you take on our Services.',
    uses: [
      {
        purpose: 'To operate our Services and present them to you in the correct format for your device.',
        basis: CONTRACT,
      },
      {
        purpose: 'To detect suspicious activity on our Services and fraudulent use of the Services.',
        basis: legitimateInterest('ensuring the security and integrity of the Services'),
      },
      {
        purpose:
          'To analyse the performance of the Services and to identify errors and ways in which we can improve the Services.',
        basis: IMPROVEMENT,
      },
      {
        purpose:
          'To tailor the Services to you and ensure they are personalised to your needs and preferences.',
        basis: PREFERENCES,
      },
      {
        purpose: 'To allow us, and our partners, to present advertising that is tailored to you.',
        basis: CONSENT,
      },
    ],
  },
  {
    category:
      'Information About Your Location, including general geographic location that we or our third-party providers may derive from your IP address.',
    uses: [
      {
        purpose: 'To detect suspicious activity on our Services and fraudulent use of the Services.',
        basis: legitimateInterest('ensuring the security and integrity of the Services'),
      },
      {
        purpose:
          'To analyse the performance of the Services and to identify errors and ways in which we can improve the Services.',
        basis: IMPROVEMENT,
      },
      {
        purpose:
          'To tailor the Services to you and ensure they are personalised to your needs and preferences.',
        basis: PREFERENCES,
      },
      {
        purpose: 'To allow us, and our partners, to present advertising that is tailored to you.',
        basis: CONSENT,
      },
    ],
  },
  {
    category:
      'Mobile Sign-On, including biometric recognition technology available through your mobile device, such as Face ID, Touch ID, or fingerprint authentication.',
    uses: [
      {
        purpose:
          'We may provide you the ability to log in to our mobile applications or authenticate yourself using biometric recognition technology available through your mobile device. We do not store or have access to biometric information. Instead, your mobile device performs the biometric authentication process and only lets us know whether the authentication was successful.',
        basis: CONSENT,
      },
    ],
  },
];

const GENERATED_DATA: DataCategory[] = [
  {
    category:
      'Enhanced Profile Information, including summaries of data collected, and analysis and insights based on your interactions with our Services.',
    uses: [
      {
        purpose:
          'To enhance your profile in order to provide you with a personalised experience as part of our Services.',
        basis: legitimateInterest('to identify and implement improvements to our Services'),
      },
    ],
  },
  {
    category:
      'Inferences, including inferences or predictions about you and your interests and preferences based on the personal data we collect about you.',
    uses: [
      {
        purpose:
          'To identify and predict your preferences over time, in order to provide you with deeper insights, as part of our Services.',
        basis: legitimateInterest('to identify and implement improvements to our Services'),
      },
    ],
  },
  {
    category: 'All of the above information.',
    uses: [
      {
        purpose:
          'To monitor and improve our analysis, evaluations, and recommendations, as part of our Services.',
        basis: legitimateInterest('to identify and implement improvements to our Services'),
      },
    ],
  },
];

const RECIPIENTS: { recipient: string; why: string }[] = [
  {
    recipient: 'To Affiliates',
    why: 'We may disclose personal data to our affiliates or related companies for purposes consistent with this Privacy Notice, where necessary to perform a contract with you or where we have a legitimate interest in operating our business efficiently.',
  },
  {
    recipient: 'To Service Providers',
    why: 'We engage third parties to perform certain services on our behalf in connection with the uses of personal data described above. Depending on the applicable services, these service providers may process personal data on our behalf or have access to personal data while performing services on our behalf. These Service Providers generally act as processors on our behalf, meaning they process personal data only on our instructions.',
  },
  {
    recipient: 'To Ad Networks and Advertising Partners',
    why: 'We may work with third-party ad networks and advertising partners to deliver advertising and personalised content on our Services and on other websites and services. These parties may collect information automatically from your browser or device when you visit our Services through the use of cookies and related technologies. We only share personal data for advertising purposes where we have your consent to do so.',
  },
  {
    recipient: 'In Connection with a Business Transaction or Reorganisation',
    why: 'We may take part in or be involved with a business transaction or reorganisation, such as a merger, acquisition, joint venture, or financing or sale of company assets. We may disclose, transfer, or assign personal data to a third party during negotiation of, in connection with, or as an asset in such a business transaction or reorganisation. We share personal data in these circumstances where we have a legitimate interest in facilitating a corporate transaction or where required to comply with contractual obligations.',
  },
  {
    recipient: 'To Facilitate Legal Obligations and Rights',
    why: 'We may disclose personal data to third parties, such as legal advisers and law enforcement: in connection with the establishment, exercise, or defence of legal claims; to comply with laws or to respond to lawful requests and legal process; to protect our rights and property and the rights and property of our agents, customers, and others; to detect, suppress, or prevent fraud; or to protect the health and safety of us, our customers, or any person. We share personal data in these circumstances where necessary to comply with a legal obligation or where we have a legitimate interest in protecting our legal rights.',
  },
];

const RIGHTS: { name: string; description: string }[] = [
  {
    name: 'Right to Access',
    description:
      'The right to obtain confirmation of whether we are processing personal data about you, and access to and a copy of the personal data we are processing about you.',
  },
  {
    name: 'Right of Portability',
    description:
      'The right to obtain a copy of the personal data we have collected about you in a structured, commonly used, and machine-readable format, and the right to transmit that personal data to another controller without hindrance.',
  },
  {
    name: 'Right to Rectification',
    description:
      'The right to correct or update any personal data about you that is inaccurate or incomplete.',
  },
  {
    name: 'Right to Restriction of Processing',
    description:
      'The right to require us to limit the purposes for which we process your personal data if the continued processing of the personal data in this way is not justified, such as where the accuracy of the personal data is contested by you.',
  },
  {
    name: 'Right to Object to Processing',
    description:
      'The right to object to any processing based on our legitimate interests where there are grounds relating to your particular situation. There may be compelling reasons for continuing to process your personal data, and we will assess and inform you if that is the case. You can object to marketing activities for any reason.',
  },
  {
    name: 'Right to Withdraw Consent',
    description:
      'The right to withdraw your previously provided consent to our processing of your personal data. Please note withdrawing your consent will not affect the lawfulness of our use of your personal data before your consent was withdrawn, nor our processing of personal data pursuant to a different lawful basis for processing.',
  },
  {
    name: 'Right to Erasure',
    description:
      'The right to have us erase your personal data if the continued processing of that personal data is not otherwise justified.',
  },
  {
    name: 'Right to Complain',
    description:
      'If you are unhappy with how we have handled your personal data or any privacy request, you have the right to submit a complaint to us.',
  },
];

const SUPERVISORY_AUTHORITIES: { region: string; authority: string; href: string }[] = [
  {
    region: 'United Kingdom',
    authority: 'Information Commissioner',
    href: 'https://ico.org.uk/make-a-complaint/',
  },
  {
    region: 'European Economic Area',
    authority: 'EEA Data Protection Authorities',
    href: 'https://edpb.europa.eu/about-edpb/board/members_en',
  },
  {
    region: 'Switzerland',
    authority: 'Federal Data Protection and Information Commissioner (FDPIC)',
    href: 'https://www.edoeb.admin.ch/edoeb/en/home/the-fdpic/contact.html',
  },
];

const PrivacyPage: React.FC<PrivacyPageProps> = ({
  onNavigateToExplore,
  onNavigateToProfile,
  onNavigateToMyBooks,
  onNavigateToLibrary,
  onNavigateHome,
  onNavigateToLogin,
  onNavigateToSignup,
  onNavigateToTerms,
  onToggleMenu,
  isAuthenticated,
}) => {
  const { theme } = useTheme();
  const isDarkMode = theme === 'dark';
  const themeClasses = isDarkMode ? 'text-theme-primary-dark' : 'bg-theme-light text-theme-primary-light';
  const textColour = isDarkMode ? 'text-theme-primary-dark' : 'text-theme-primary-light';
  const linkClasses = `underline hover:opacity-80 transition-opacity ${textColour}`;
  const borderColour = isDarkMode ? 'border-gray-600' : 'border-gray-300';

  const SectionHeading: React.FC<{ children: React.ReactNode }> = ({ children }) => (
    <h2 className={`portrait-text-app text-2xl font-semibold mb-4 ${textColour}`}>{children}</h2>
  );

  const SubHeading: React.FC<{ children: React.ReactNode }> = ({ children }) => (
    <h3 className={`portrait-text-app text-xl font-semibold mb-3 ${textColour}`}>{children}</h3>
  );

  // Tables carry three long columns, so they scroll sideways on a phone rather
  // than squeezing the page itself.
  const DataTable: React.FC<{ rows: DataCategory[] }> = ({ rows }) => (
    <div className="overflow-x-auto">
      <table className={`w-full min-w-3xl border-collapse border ${borderColour} text-base`}>
        <thead>
          <tr>
            {['Category of Personal Data', 'How we use it', 'Legal basis for processing'].map((header) => (
              <th
                key={header}
                scope="col"
                className={`border ${borderColour} p-3 text-left align-top font-semibold w-1/3`}
              >
                {header}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.map(({ category, uses }) =>
            uses.map((use, index) => (
              <tr key={`${category}-${use.purpose}`}>
                {index === 0 && (
                  <th
                    scope="row"
                    rowSpan={uses.length}
                    className={`border ${borderColour} p-3 text-left align-top font-normal`}
                  >
                    {category}
                  </th>
                )}
                <td className={`border ${borderColour} p-3 align-top`}>{use.purpose}</td>
                <td className={`border ${borderColour} p-3 align-top`}>{use.basis}</td>
              </tr>
            ))
          )}
        </tbody>
      </table>
    </div>
  );

  return (
    <div
      className={`w-full ${themeClasses} transition-colors duration-300`}
      style={{
        backgroundColor: isDarkMode
          ? 'var(--background-colour-dark-mode)'
          : 'var(--background-colour-light-mode)',
      }}
    >
      <TopNavigation
        isDarkMode={isDarkMode}
        onToggleMenu={onToggleMenu}
        onNavigateHome={onNavigateHome}
        onNavigateToExplore={onNavigateToExplore}
        onNavigateToLibrary={onNavigateToLibrary}
        onNavigateToLogin={(source) => onNavigateToLogin?.(source)}
        onNavigateToSignup={(source) => onNavigateToSignup?.(source)}
        onNavigateToProfile={onNavigateToProfile}
        onNavigateToMyBooks={onNavigateToMyBooks}
        showProfileIcon={isAuthenticated}
      />

      {/* Main Content - Single Column Layout */}
      <div className="w-full flex flex-col px-8 lg:px-16 xl:px-24 pb-[calc(10rem+var(--safe-bottom))]">
        <div className="w-full max-w-5xl mx-auto">

          {/* Page Header */}
          <header className="text-left w-full my-24 max-w-5xl mx-auto">
            <h2
              className={`portrait-text-app mb-4 text-7xl lg:text-8xl xl:text-9xl font-bold leading-tight line-height-.2 -mt-1 ${textColour}`}
            >
              Privacy Notice
            </h2>
            <p className={`text-xl ${textColour}`}>
              Welcome to hi-aleph. We operate an interactive audiobook mobile application that allows you to listen to audiobooks and ask questions about the book content using voice comments (the &quot;App&quot;).
            </p>
            <p className={`text-lg mt-4 ${textColour}`}>Last updated: 15 September 2026</p>

            {/* Colored Element */}
            <div className="flex justify-end my-2">
              <div className={`w-48 h-20 ${textColour}`}></div>
            </div>
          </header>

          {/* Privacy Notice Content */}
          <div className={`prose prose-lg max-w-none pb-18 ${isDarkMode ? 'prose-invert' : ''}`}>
            <div className={`space-y-8 ${textColour}`}>

              <section>
                <SectionHeading>1. Introduction</SectionHeading>
                <p className="text-lg leading-relaxed">
                  This Privacy Notice explains how Delphi Art Technology Limited (&quot;hi-aleph,&quot; &quot;we,&quot; &quot;us,&quot; &quot;our&quot;) collects, uses, discloses, and otherwise processes personal data when you download and use our App or interact with our website at <a href="https://hi-aleph.com/" className={linkClasses}>https://hi-aleph.com/</a> (the &quot;Website&quot;). In this Privacy Notice, we refer to the App and the Website collectively as the &quot;Services&quot;.
                </p>
              </section>

              <section>
                <SectionHeading>2. Who is responsible for your personal data</SectionHeading>
                <p className="text-lg leading-relaxed mb-4">
                  hi-aleph is the controller of the personal data that you submit or that we collect when you use our Services. This means that we determine and are responsible for how your personal data is used.
                </p>

                <div>
                  <SubHeading>2.1 Contact us</SubHeading>
                  <p className="text-lg leading-relaxed">
                    If you would like to contact us about anything in this Privacy Notice or how we use your personal data, please email us at <a href="mailto:say@hi-aleph.com" className={linkClasses}>say@hi-aleph.com</a>.
                  </p>
                </div>
              </section>

              <section>
                <SectionHeading>3. Personal Data We Collect</SectionHeading>
                <p className="text-lg leading-relaxed mb-4">
                  We collect personal data in two main ways: (i) personal data that you submit directly to us when you use our Services, such as when you set up an account on our App or contact us; and (ii) personal data that we collect automatically about how you access and use our Services, including through cookies and similar tracking technologies. For more detailed information about the cookies we use, including details of how to opt out, please see the <a href="#cookies" className={linkClasses}>Cookies and Other Tracking Technologies</a> section below. We collect and use this personal data as follows:
                </p>
                <DataTable rows={COLLECTED_DATA} />
              </section>

              <section>
                <SectionHeading>4. Personal Data We Create or Generate</SectionHeading>
                <p className="text-lg leading-relaxed mb-4">
                  We may also generate and create inferences and analysis in connection with the personal data we collect as set out below.
                </p>
                <DataTable rows={GENERATED_DATA} />
              </section>

              <section>
                <SectionHeading>5. Our Disclosure of Personal Data</SectionHeading>
                <p className="text-lg leading-relaxed mb-4">
                  We disclose or otherwise make available personal data in the following ways. In each case, we only share personal data where we have a lawful basis to do so, such as where necessary to perform a contract with you, where required by law, or where we have a legitimate interest in doing so:
                </p>
                <div className="overflow-x-auto">
                  <table className={`w-full min-w-2xl border-collapse border ${borderColour} text-base`}>
                    <thead>
                      <tr>
                        <th scope="col" className={`border ${borderColour} p-3 text-left align-top font-semibold w-1/3`}>
                          Recipients
                        </th>
                        <th scope="col" className={`border ${borderColour} p-3 text-left align-top font-semibold`}>
                          Why we share your personal information with these recipients
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      {RECIPIENTS.map(({ recipient, why }) => (
                        <tr key={recipient}>
                          <th scope="row" className={`border ${borderColour} p-3 text-left align-top font-normal`}>
                            {recipient}
                          </th>
                          <td className={`border ${borderColour} p-3 align-top`}>{why}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </section>

              <section>
                <SectionHeading>6. Security of Personal Data</SectionHeading>
                <p className="text-lg leading-relaxed">
                  We have implemented reasonable physical, technical, and organisational safeguards that are designed to protect your personal data. However, despite these controls, we cannot completely ensure or warrant the security of your personal data.
                </p>
              </section>

              <section>
                <SectionHeading>7. Retention of Personal Data</SectionHeading>
                <p className="text-lg leading-relaxed mb-4">
                  We will retain the personal data we collect about you for no longer than is necessary for the purposes for which it was collected. The specific retention periods depend on the nature of the data and our relationship with you, as follows:
                </p>
                <ul className="list-disc list-inside space-y-2 ml-4">
                  <li><strong>Account and Profile Data:</strong> We retain your account information for the duration of your account plus 6 years after account closure to handle any queries or disputes.</li>
                  <li><strong>Voice Data and User Content:</strong> Voice recordings are retained for 30 days to process your requests and are then deleted, unless you have consented to their use for service improvement, in which case they are retained for 6 years.</li>
                  <li><strong>Payment Information:</strong> We retain transaction records for 6 years after the transaction to comply with tax and accounting obligations.</li>
                  <li><strong>Usage and Device Data:</strong> We retain automatically collected data for 1 month for analytics purposes, unless a longer retention period is required for security or fraud prevention.</li>
                </ul>
                <p className="text-lg leading-relaxed mt-4">
                  In certain circumstances, we may need to apply a &quot;legal hold&quot; that retains information beyond our typical retention period where we face threat of legal claim. In that case, we will retain the information until the hold is removed, which typically means the claim or threat of claim has been resolved.
                </p>
                <p className="text-lg leading-relaxed mt-4">
                  Once retention of the personal data is no longer reasonably necessary for the purposes outlined above, we will either delete or anonymise the personal data or, if that is not possible (for example, because personal data has been stored in backup archives), we will securely store the personal data and isolate it from further active processing until deletion or anonymisation is possible.
                </p>
              </section>

              <section id="cookies">
                <SectionHeading>8. Cookies and Other Tracking Technologies</SectionHeading>
                <p className="text-lg leading-relaxed mb-4">
                  We use automatic data collection technologies (including cookies) in connection with our Services. For all but essential automatic data collection technologies, we only process your personal data in this way to the extent that you have given us your consent to do so.
                </p>
                <p className="text-lg leading-relaxed">
                  To withdraw your consent to our use of non-essential automatic data collection technologies, please use the &quot;Cookies&quot; link in the footer of our Website, or the Product analytics setting in your profile on the App, and adjust your preferences accordingly. Please note this preferences tool is website-, device-, and browser-specific, so you will need to change your preferences on each device and browser you use to interact with the Services.
                </p>
              </section>

              <section>
                <SectionHeading>9. Marketing and Communications</SectionHeading>
                <p className="text-lg leading-relaxed mb-4">
                  From time to time we may contact you with information about our Services, including sending you marketing messages and asking for your feedback on our Services.
                </p>
                <p className="text-lg leading-relaxed mb-4">
                  For some marketing messages, we may use personal data we collect about you to help us determine the most relevant marketing information to share with you.
                </p>
                <p className="text-lg leading-relaxed">
                  We will only send you marketing communications to the extent we have your consent to do so. You can opt out of marketing messages at any time by clicking on the unsubscribe link in the footer of our marketing emails or by amending your account settings. We make every effort to promptly process all unsubscribe requests. You may not opt out of service-related communications (e.g. account verification, transactional communications, changes or updates to the features of the Services, technical and security notices).
                </p>
              </section>

              <section>
                <SectionHeading>10. Automated Decision-Making and Profiling</SectionHeading>
                <p className="text-lg leading-relaxed">
                  We do not conduct automated processing of personal data, including profiling, for the purposes of making decisions that produce legal or similarly significant effects concerning you.
                </p>
              </section>

              <section>
                <SectionHeading>11. International Transfers of Personal Data</SectionHeading>
                <p className="text-lg leading-relaxed mb-4">
                  The personal data we collect may be transferred to and stored in countries outside of the jurisdiction you are in where we and our third-party service providers and partners have operations. Your personal data may be processed outside of the UK, EEA, or Switzerland. We will ensure that these international transfers of your personal data are made pursuant to appropriate safeguards, such as:
                </p>
                <ul className="list-disc list-inside space-y-2 ml-4">
                  <li>Transfer to countries recognised under the UK Data Protection Act 2018 or by the UK Secretary of State, the European Commission, or the Swiss Federal Council (as applicable) as offering an equivalent level of protection as compared to the level of protection in the country you are located (an &quot;adequacy decision&quot;).</li>
                  <li>Transfer to organisations holding a certification to the EU-U.S. and Swiss-U.S. Data Privacy Framework and UK Extension (&quot;Data Privacy Framework&quot;), a framework designed by the U.S. Department of Commerce and the European Commission (and Swiss Federal Council and UK Secretary of State) to ensure adequate protection for personal data transferred to a company participating in the Data Privacy Framework.</li>
                  <li>Transfer to a third party with whom we have entered into standard contractual clauses, which are recognised as offering adequate protection for the rights and freedoms of data subjects, as adopted under the UK Data Protection Act 2018, by the Swiss data protection regulator (FDPIC), or by the European Commission (as applicable).</li>
                </ul>
                <p className="text-lg leading-relaxed mt-4 mb-4">
                  We may transfer your personal data to, or store your personal data in, the following countries:
                </p>
                <div className="overflow-x-auto">
                  <table className={`w-full min-w-2xl border-collapse border ${borderColour} text-base`}>
                    <thead>
                      <tr>
                        <th scope="col" className={`border ${borderColour} p-3 text-left align-top font-semibold w-1/3`}>
                          Country
                        </th>
                        <th scope="col" className={`border ${borderColour} p-3 text-left align-top font-semibold`}>
                          Appropriate Safeguard
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      <tr>
                        <th scope="row" className={`border ${borderColour} p-3 text-left align-top font-normal`}>
                          United States
                        </th>
                        <td className={`border ${borderColour} p-3 align-top`}>
                          We either: (i) enter into agreements with recipients that incorporate standard contractual clauses; or (ii) ensure that recipients are certified to the Data Privacy Framework.
                        </td>
                      </tr>
                    </tbody>
                  </table>
                </div>
                <p className="text-lg leading-relaxed mt-4">
                  We will take appropriate steps to ensure that your personal data is treated securely and in accordance with applicable law and this Privacy Notice regardless of where it is processed. If you wish to enquire further about the safeguards we use, please contact us using the details set out in the Contact us section above.
                </p>
              </section>

              <section>
                <SectionHeading>12. Children&rsquo;s Personal Data</SectionHeading>
                <p className="text-lg leading-relaxed">
                  Our Services are not directed to, and we do not intend to, or knowingly, collect or solicit personal data from children under the age of 16. If an individual is under the age of 16, they should not use our Services or otherwise provide us with any personal data either directly or by other means. If a child under the age of 16 has provided personal data to us, we encourage the child&rsquo;s parent or guardian to contact us to request that we remove the personal data from our systems. If we learn that any personal data we collect has been provided by a child under the age of 16, we will promptly delete that personal data.
                </p>
              </section>

              <section>
                <SectionHeading>13. Your Privacy Rights</SectionHeading>
                <p className="text-lg leading-relaxed mb-4">
                  Subject to certain limitations at law, you may be able to exercise the following rights in relation to your personal data:
                </p>
                <ul className="list-disc list-inside space-y-2 ml-4">
                  {RIGHTS.map(({ name, description }) => (
                    <li key={name}><strong>{name}:</strong> {description}</li>
                  ))}
                </ul>
                <p className="text-lg leading-relaxed mt-4 mb-4">
                  You also have the right to lodge a complaint with a data protection supervisory authority, in particular in the UK, EEA member state, or Switzerland where you reside, work, or where an alleged infringement of data protection law has occurred. The relevant supervisory authorities are:
                </p>
                <ul className="list-disc list-inside space-y-2 ml-4">
                  {SUPERVISORY_AUTHORITIES.map(({ region, authority, href }) => (
                    <li key={region}>
                      <strong>{region}:</strong>{' '}
                      <a href={href} target="_blank" rel="noopener noreferrer" className={linkClasses}>
                        {authority}
                      </a>
                    </li>
                  ))}
                </ul>
                <p className="text-lg leading-relaxed mt-4">
                  Please note that if the exercise of these rights limits our ability to process personal data, we may not be able to provide our Services to you, or otherwise engage with you in the same manner.
                </p>
              </section>

              <section>
                <SectionHeading>14. Submitting Privacy Rights Requests</SectionHeading>
                <p className="text-lg leading-relaxed mb-4">
                  Please submit a request specifying the right you wish to exercise by emailing us at <a href="mailto:say@hi-aleph.com" className={linkClasses}>say@hi-aleph.com</a>.
                </p>
                <p className="text-lg leading-relaxed mb-4">
                  To exercise your right to object or withdraw consent as it relates to the use of automatic data collection technologies (including cookies), please refer to the steps set forth in the <a href="#cookies" className={linkClasses}>Cookies and Other Tracking Technologies</a> section of this Privacy Notice.
                </p>
                <p className="text-lg leading-relaxed mb-4">
                  You can withdraw your consent to marketing materials by clicking on the unsubscribe link in the footer of our marketing emails or by contacting us at <a href="mailto:say@hi-aleph.com" className={linkClasses}>say@hi-aleph.com</a>. We make every effort to promptly process all unsubscribe requests. You may not opt out of service-related communications (e.g. account verification, transactional communications, changes or updates to the features of the Services, technical and security notices).
                </p>
                <p className="text-lg leading-relaxed mb-4">
                  Before processing your request to exercise certain rights (taking into account the confidential nature of any personal data we maintain), we will need to verify your identity. In order to verify your identity, we will generally either require the successful authentication of your account, or the matching of sufficient information you provide us to the information we maintain about you in our systems.
                </p>
                <p className="text-lg leading-relaxed">
                  In certain circumstances, we may decline or limit your request, particularly where we are unable to verify your identity as needed to protect your personal data or locate your information in our systems.
                </p>
              </section>

              <section>
                <SectionHeading>15. Third-Party Websites and Services</SectionHeading>
                <p className="text-lg leading-relaxed">
                  Our Services may include links to third-party websites, plug-ins, applications, and other services. Except where we post, link to, or expressly adopt or refer to this Privacy Notice, this Privacy Notice does not apply to any personal data practices of third parties. To learn about the personal data practices of third parties, please visit their respective privacy notices.
                </p>
              </section>

              <section>
                <SectionHeading>16. Updates to This Privacy Notice</SectionHeading>
                <p className="text-lg leading-relaxed mb-4">
                  We may update this Privacy Notice from time to time. When we make changes to this Privacy Notice, we will change the date at the beginning of this Privacy Notice. If we make material changes to this Privacy Notice, we will notify individuals by email to their registered email address, by prominent posting on this website or our other platforms, or through other appropriate communication channels. All changes shall be effective from the date of publication unless otherwise provided.
                </p>
                {onNavigateToTerms && (
                  <p className="text-lg leading-relaxed">
                    Your use of the Services is also governed by our{' '}
                    <button
                      type="button"
                      onClick={onNavigateToTerms}
                      className={`cursor-pointer ${linkClasses}`}
                    >
                      Terms and Conditions
                    </button>
                    .
                  </p>
                )}
              </section>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PrivacyPage;
